import { useEffect, useState } from "react";
import { Slider } from "./ui/slider";
import { Wallet, Home, Utensils, Bus, Plug, Sparkles, MoreHorizontal } from "lucide-react";
import { fetchBudgets, fetchRates } from "../lib/api";
import { useT } from "../i18n/I18nProvider";
import DataErrorNotice from "./DataErrorNotice";

const ITEMS = [
  { key: "rent", icon: Home, max: 12000 },
  { key: "food", icon: Utensils, max: 4000 },
  { key: "transport", icon: Bus, max: 1500 },
  { key: "utilities", icon: Plug, max: 2000 },
  { key: "leisure", icon: Sparkles, max: 3000 },
  { key: "other", icon: MoreHorizontal, max: 2000 },
];

export default function BudgetCalculator({ defaultCity = "varsovie" }) {
  const { t } = useT();
  const [city, setCity] = useState(defaultCity);
  const [budgets, setBudgets] = useState({});
  const [values, setValues] = useState({ rent: 3800, food: 1000, transport: 130, utilities: 880, leisure: 500, other: 350 });
  const [rates, setRates] = useState(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    fetchBudgets()
      .then((data) => {
        const map = {};
        data.forEach((b) => { map[b.city_slug] = b; });
        setBudgets(map);
        if (map[city]) setFromBudget(map[city]);
        try { const saved = JSON.parse(localStorage.getItem("hejpoland_budget")); if (saved) { setValues(saved.values); setCity(saved.city); } } catch {/* ignored */}
      })
      .catch((e) => {
        console.error("[BudgetCalculator] fetchBudgets failed:", e?.message || e);
        setLoadError(true);
      });
    fetchRates().then((r) => setRates(r.rates)).catch((e) => console.error("[BudgetCalculator] fetchRates failed:", e?.message || e));
  }, []);

  const setFromBudget = (b) => setValues({ rent: b.rent, food: b.food, transport: b.transport, utilities: b.utilities, leisure: b.leisure, other: b.other });

  const onCity = (slug) => {
    setCity(slug);
    if (budgets[slug]) setFromBudget(budgets[slug]);
  };

  const update = (k, v) => {
    const next = { ...values, [k]: v[0] };
    setValues(next);
    localStorage.setItem("hejpoland_budget", JSON.stringify({ city, values: next }));
  };

  const total = Object.values(values).reduce((a, b) => a + b, 0);
  const toEur = (pln) => rates?.EUR ? (pln / rates.EUR.rate).toFixed(0) : "—";
  const polishCities = Object.values(budgets).filter((b) => b.is_polish);

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8" data-testid="budget-calculator">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#0056D2]/10 flex items-center justify-center">
            <Wallet className="w-5 h-5 text-[#0056D2]" />
          </div>
          <div>
            <h3 className="font-display font-semibold text-xl text-slate-900">{t("budget.title")}</h3>
            <p className="text-sm text-slate-500">{t("budget.sub")}</p>
          </div>
        </div>
        <select value={city} onChange={(e) => onCity(e.target.value)} className="px-4 py-2 border border-slate-200 rounded-full bg-white text-sm font-medium" data-testid="budget-city-select">
          {polishCities.map((c) => <option key={c.city_slug} value={c.city_slug}>{c.city_name}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {loadError && (
          <div className="md:col-span-2">
            <DataErrorNotice onRetry={() => window.location.reload()} />
          </div>
        )}
        {ITEMS.map((it) => {
          const Icon = it.icon;
          return (
            <div key={it.key} className="bg-slate-50 rounded-xl p-4" data-testid={`budget-item-${it.key}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2.5">
                  <Icon className="w-4 h-4 text-slate-500" />
                  <span className="text-sm font-medium text-slate-700">{t(`budget.items.${it.key}`)}</span>
                </div>
                <span className="text-sm font-semibold text-slate-900 tabular-nums">{values[it.key]} zł</span>
              </div>
              <Slider value={[values[it.key]]} onValueChange={(v) => update(it.key, v)} min={0} max={it.max} step={50} data-testid={`budget-slider-${it.key}`} />
            </div>
          );
        })}
      </div>

      <div className="mt-6 pt-6 border-t border-slate-100 flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-sm text-slate-500">{t("budget.total")} {budgets[city]?.city_name || ""}</p>
          <p className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tabular-nums" data-testid="budget-total">{total.toLocaleString("fr-FR")} <span className="text-xl text-slate-500">zł</span></p>
          {rates?.EUR && <p className="text-sm text-slate-500 mt-0.5" data-testid="budget-total-eur">≈ {toEur(total)} € {t("budget.perMonth")}</p>}
        </div>
        <button onClick={() => budgets[city] && setFromBudget(budgets[city])} className="text-sm text-[#0056D2] hover:underline" data-testid="budget-reset">
          {t("budget.reset")}
        </button>
      </div>
    </div>
  );
}
